import { useEffect, useRef } from 'react';
import { Shield, Clock, Users, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Benefit {
  title: string;
  description: string;
}

export function WhyChooseUs() {
  const { t } = useTranslation();
  const benefitsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const element = entry.target as HTMLElement;
          if (entry.isIntersecting) {
            element.classList.add('opacity-100', 'translate-y-0');
            element.classList.remove('opacity-0', 'translate-y-20');
          } else {
            element.classList.remove('opacity-100', 'translate-y-0');
            element.classList.add('opacity-0', 'translate-y-20');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    const handleScroll = () => {
      if (benefitsRef.current) {
        const cards = Array.from(benefitsRef.current.children);
        cards.forEach((card) => {
          const element = card as HTMLElement;
          const rect = element.getBoundingClientRect();
          const scrollPercent = (window.innerHeight - rect.top) / window.innerHeight;
          if (scrollPercent > 0 && scrollPercent < 1) {
            const translateY = (1 - scrollPercent) * 50;
            element.style.transform = `translateY(${translateY}px)`;
          }
        });
      }
    };

    const cards = benefitsRef.current?.children;
    if (cards) {
      Array.from(cards).forEach((card, index) => {
        const element = card as HTMLElement;
        element.classList.add('opacity-0', 'translate-y-20', 'transition-all', 'duration-1000', 'ease-out');
        element.style.transitionDelay = `${index * 150}ms`;
        observer.observe(card);
      });
    }

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const icons = [
    <Clock key="clock" className="w-8 h-8" />,
    <Shield key="shield" className="w-8 h-8" />,
    <Users key="users" className="w-8 h-8" />,
    <Zap key="zap" className="w-8 h-8" />
  ];

  // Définir un tableau fallback au cas où les traductions ne fonctionnent pas
  const fallbackBenefits: Benefit[] = [
    {
      title: "Rapidité d'exécution",
      description: "Nous délivrons votre projet dans les délais convenus"
    },
    {
      title: "Sécurité garantie",
      description: "Nous prenons la sécurité de vos données très au sérieux"
    },
    {
      title: "Support client",
      description: "Notre équipe est disponible pour répondre à vos questions"
    },
    {
      title: "Performance optimale",
      description: "Nos solutions sont optimisées pour des performances maximales"
    }
  ];

  // Récupérer les benefits depuis les traductions avec sécurité
  let benefits: Benefit[] = [];
  try {
    // Vérifier si la traduction retourne bien un tableau
    const translatedBenefits = t('benefits', { returnObjects: true });
    
    if (Array.isArray(translatedBenefits)) {
      // Vérifier que chaque élément du tableau correspond à l'interface Benefit
      benefits = translatedBenefits.filter((item): item is Benefit => 
        typeof item === 'object' && 
        item !== null && 
        'title' in item && 
        'description' in item &&
        typeof item.title === 'string' && 
        typeof item.description === 'string'
      );

      // Si après filtrage nous n'avons pas assez de benefits, utiliser les fallbacks
      if (benefits.length < fallbackBenefits.length) {
        console.warn("Some translated benefits don't match the required format");
        benefits = fallbackBenefits;
      }
    } else {
      console.warn("Translation 'benefits' is not an array:", translatedBenefits);
      benefits = fallbackBenefits;
    }
  } catch (error) {
    console.error("Error getting translations:", error);
    benefits = fallbackBenefits;
  }

  return (
    <section id="us" className="py-12 bg-white dark:bg-black text-gray-900 dark:text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">
            {t('why_choose_us_title')}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            {t('why_choose_us_subtitle')}
          </p>
        </div>

        <div 
          ref={benefitsRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {benefits.map((benefit: Benefit, index: number) => (
            <div
              key={index}
              className="bg-gray-100 dark:bg-black dark:from-black dark:to-black backdrop-blur-sm p-6 rounded-xl shadow-lg hover:transform hover:-translate-y-1 transition-all will-change-transform"
            >
              <div className="text-blue-600 dark:text-blue-400 mb-4">
                {icons[index]}
              </div>
              <h3 className="text-xl font-semibold mb-2">
                {benefit.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}